import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ChevronLeft, ChevronRight, BookOpen, CheckCircle, Volume2, VolumeX, Clock } from 'lucide-react';
import { toast } from 'sonner';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = process.env.REACT_APP_BACKEND_URL;

const VOICE_OPTIONS = [
  { id: 'alloy', name: 'Alloy', description: 'Neutral & balanced' },
  { id: 'echo', name: 'Echo', description: 'Warm & conversational' },
  { id: 'fable', name: 'Fable', description: 'Expressive & dramatic' },
  { id: 'onyx', name: 'Onyx', description: 'Deep & authoritative' },
  { id: 'nova', name: 'Nova', description: 'Friendly & upbeat' },
  { id: 'shimmer', name: 'Shimmer', description: 'Clear & bright' },
  { id: 'ash', name: 'Ash', description: 'Calm & measured' },
  { id: 'coral', name: 'Coral', description: 'Smooth & professional' },
  { id: 'sage', name: 'Sage', description: 'Wise & thoughtful' },
];

export default function CourseIntroductionPage() {
  const navigate = useNavigate();
  const [currentPage, setCurrentPage] = useState(1);
  const [pageData, setPageData] = useState(null);
  const [pageInfo, setPageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeSpent, setTimeSpent] = useState(0);
  
  // Narrator state
  const [isNarrating, setIsNarrating] = useState(false);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [selectedVoice, setSelectedVoice] = useState('onyx');
  const audioRef = useRef(null);
  const speechRef = useRef(null);
  
  const timerRef = useRef(null);
  const syncTimerRef = useRef(null);
  const serverTimeRef = useRef(0);
  const lastSyncedTimeRef = useRef(0);

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${sessionStorage.getItem('token')}`
  });

  // Sync time to server
  const syncTimeToServer = useCallback(async () => {
    const totalTime = serverTimeRef.current + timeSpent;
    if (totalTime > lastSyncedTimeRef.current) {
      try {
        await axios.post(`${API}/api/chapters/introduction/time`, 
          { time_spent_seconds: totalTime },
          { headers: getAuthHeaders() }
        );
        lastSyncedTimeRef.current = totalTime;
      } catch (err) {
        console.log('Could not sync time:', err);
      }
    }
  }, [timeSpent]);

  // Start timer on mount
  useEffect(() => {
    timerRef.current = setInterval(() => {
      setTimeSpent(prev => prev + 1);
    }, 1000);
    
    // Sync time every 30 seconds
    syncTimerRef.current = setInterval(() => {
      syncTimeToServer();
    }, 30000);
    
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (syncTimerRef.current) clearInterval(syncTimerRef.current);
      syncTimeToServer();
      // Stop audio on unmount
      if (audioRef.current) {
        audioRef.current.pause();
      }
      if (speechRef.current) {
        window.speechSynthesis.cancel();
      }
    };
  }, [syncTimeToServer]);

  useEffect(() => {
    fetchPageInfo();
  }, []);

  useEffect(() => {
    if (pageInfo) {
      fetchPage(currentPage);
    }
  }, [currentPage, pageInfo]);

  // Stop narration when page changes
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (speechRef.current) {
      window.speechSynthesis.cancel();
    }
    setIsNarrating(false);
  }, [currentPage]);

  const fetchPageInfo = async () => {
    try {
      const res = await axios.get(`${API}/api/chapters/introduction/pages/info`, {
        headers: getAuthHeaders()
      });
      setPageInfo(res.data);
      setLoading(false);
    } catch (err) {
      console.error('Failed to fetch intro info:', err);
      setLoading(false);
    }
  };

  const fetchPage = async (pageNum) => {
    try {
      const res = await axios.get(`${API}/api/chapters/introduction/pages/${pageNum}`, {
        headers: getAuthHeaders()
      });
      setPageData(res.data);
    } catch (err) {
      console.error('Failed to fetch page:', err);
    }
  };

  const handleNextPage = () => {
    if (currentPage < pageInfo?.total_pages) {
      setCurrentPage(prev => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleStartCourse = async () => {
    try {
      await syncTimeToServer();
      await axios.post(`${API}/api/chapters/introduction/complete`, {}, {
        headers: getAuthHeaders()
      });
      toast.success('Introduction completed! Starting Chapter 1...');
      navigate('/dashboard');
    } catch (err) {
      console.error('Failed to complete introduction:', err);
      navigate('/dashboard');
    }
  };

  // Narrator functions
  const toggleNarration = async () => {
    if (isNarrating) {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      window.speechSynthesis.cancel();
      setIsNarrating(false);
      return;
    }

    const contentDiv = document.querySelector('.page-content');
    if (!contentDiv) return;

    const textContent = contentDiv.innerText;
    const truncatedText = textContent.slice(0, 4096);
    
    if (truncatedText.length < 10) {
      toast.error('Not enough text content to narrate');
      return;
    }

    setIsLoadingAudio(true);
    
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API}/api/tts/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: truncatedText,
          voice: selectedVoice,
          speed: 1.0
        })
      });

      if (!response.ok) {
        throw new Error('TTS generation failed');
      }

      const data = await response.json();
      
      if (data.audio_base64) {
        const audio = new Audio(`data:audio/mp3;base64,${data.audio_base64}`);
        audioRef.current = audio;
        
        audio.onended = () => setIsNarrating(false);
        audio.onerror = () => {
          setIsNarrating(false);
          toast.error('Audio playback error');
        };
        
        await audio.play();
        setIsNarrating(true);
        toast.success('AI Narrator started');
      }
    } catch (error) {
      console.error('TTS error:', error);
      toast.info('Using browser narrator (AI voice unavailable)');
      fallbackToWebSpeech(textContent);
    } finally {
      setIsLoadingAudio(false);
    }
  };

  const fallbackToWebSpeech = (text) => {
    const utterance = new SpeechSynthesisUtterance(text.slice(0, 5000));
    utterance.rate = 0.9;
    utterance.pitch = 1;

    const voices = window.speechSynthesis.getVoices();
    const preferredVoice = voices.find(v => v.name.includes('Daniel') || v.name.includes('Google US English'));
    if (preferredVoice) utterance.voice = preferredVoice;

    utterance.onend = () => setIsNarrating(false);
    utterance.onerror = () => setIsNarrating(false);

    speechRef.current = utterance;
    window.speechSynthesis.speak(utterance);
    setIsNarrating(true);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-600">Loading introduction...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-[#1e3a5f] text-white sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4">
          <div className="flex items-center justify-between py-3">
            <Button 
              variant="ghost" 
              onClick={async () => {
                await syncTimeToServer();
                navigate('/dashboard');
              }}
              className="text-white hover:bg-white/10"
              data-testid="back-to-dashboard"
            >
              <ChevronLeft className="w-5 h-5 mr-1" />
              Dashboard
            </Button>
            
            <div className="text-center">
              <h1 className="text-lg font-semibold">Course Introduction</h1>
              <p className="text-sm text-white/70">Page {currentPage} of {pageInfo?.total_pages || 1}</p>
            </div>
            
            <div className="flex items-center gap-2 text-sm">
              <Clock className="w-4 h-4" />
              <span>{formatTime(timeSpent)}</span>
            </div>
          </div>
          
          {/* Progress bar */}
          <div className="h-1 bg-white/20 -mx-4">
            <div 
              className="h-full bg-[#f5a623] transition-all duration-300"
              style={{ width: `${(currentPage / (pageInfo?.total_pages || 1)) * 100}%` }}
            />
          </div>
        </div>
      </div>

      {/* Narrator Controls */}
      <div className="bg-gray-100 border-b">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-gray-700">AI Narrator:</span>
              <Select value={selectedVoice} onValueChange={setSelectedVoice}>
                <SelectTrigger className="w-40 h-9 bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VOICE_OPTIONS.map(voice => (
                    <SelectItem key={voice.id} value={voice.id}>
                      {voice.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button
              onClick={toggleNarration}
              disabled={isLoadingAudio}
              variant={isNarrating ? "destructive" : "default"}
              className={isNarrating ? "" : "bg-[#1e3a5f] hover:bg-[#2d4a6f]"}
            >
              {isLoadingAudio ? (
                <span className="animate-pulse">Loading...</span>
              ) : isNarrating ? (
                <>
                  <VolumeX className="w-4 h-4 mr-2" />
                  Stop Narrator
                </>
              ) : (
                <>
                  <Volume2 className="w-4 h-4 mr-2" />
                  Start Narrator
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {pageData && (
          <div 
            className="page-content prose max-w-none
              prose-headings:text-[#1e3a5f] prose-headings:font-bold
              prose-h1:text-3xl prose-h1:mb-2
              prose-h2:text-2xl prose-h2:text-[#1e3a5f] prose-h2:mt-8 prose-h2:mb-4 prose-h2:border-b prose-h2:border-gray-200 prose-h2:pb-2
              prose-h3:text-xl prose-h3:text-[#1e3a5f] prose-h3:mt-6
              prose-h4:text-lg prose-h4:text-[#1e3a5f]
              prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-4
              prose-li:text-gray-700
              prose-strong:text-[#1e3a5f]
              prose-table:border-collapse
              prose-th:bg-[#1e3a5f] prose-th:text-white prose-th:p-3
              prose-td:border prose-td:border-gray-200 prose-td:p-3
            "
            dangerouslySetInnerHTML={{ __html: pageData.content }}
          />
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between mt-12 pt-8 border-t border-gray-200">
          <Button
            onClick={handlePrevPage}
            disabled={currentPage <= 1}
            variant="outline"
            className="border-gray-300 disabled:opacity-30"
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            Previous
          </Button>

          <div className="flex gap-2">
            {Array.from({ length: pageInfo?.total_pages || 1 }, (_, i) => (
              <button
                key={i}
                onClick={() => {
                  setCurrentPage(i + 1);
                  window.scrollTo(0, 0);
                }}
                className={`w-3 h-3 rounded-full transition-colors ${
                  currentPage === i + 1 ? 'bg-[#f5a623]' : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          {currentPage < (pageInfo?.total_pages || 1) ? (
            <Button
              onClick={handleNextPage}
              className="bg-[#1e3a5f] hover:bg-[#2d4a6f] text-white"
            >
              Next
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleStartCourse}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              <BookOpen className="w-5 h-5 mr-2" />
              Start Chapter 1
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
